import {
  ChatAuthError,
  RateLimitedError,
  type Citation,
  type SendMessageResult,
  type SessionResponse,
  type StepInfo,
} from "../types";

export class HttpTransport {
  constructor(private readonly baseUrl: string, private readonly timeoutMs: number) {}

  private requestController(externalSignal?: AbortSignal): {
    controller: AbortController;
    clearConnectionTimeout: () => void;
    cleanup: () => void;
  } {
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), this.timeoutMs);
    const forwardAbort = () => controller.abort(externalSignal?.reason);
    if (externalSignal) {
      if (externalSignal.aborted) {
        forwardAbort();
      } else {
        externalSignal.addEventListener("abort", forwardAbort, { once: true });
      }
    }
    return {
      controller,
      clearConnectionTimeout: () => globalThis.clearTimeout(timeoutId),
      cleanup: () => {
        globalThis.clearTimeout(timeoutId);
        externalSignal?.removeEventListener("abort", forwardAbort);
      },
    };
  }

  private async parseError(res: Response): Promise<string> {
    const body = await res.text();
    return body.replace(/\s+/g, " ").trim().slice(0, 400);
  }

  async createSession(siteId: string): Promise<SessionResponse> {
    const request = this.requestController();
    try {
      const res = await fetch(new URL("/api/session", this.baseUrl), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ site_id: siteId }),
        signal: request.controller.signal,
        credentials: "omit",
      });
      request.clearConnectionTimeout();
      if (!res.ok) {
        const detail = await this.parseError(res);
        throw new ChatAuthError(`Session failed: ${res.status} ${detail}`);
      }
      return (await res.json()) as SessionResponse;
    } finally {
      request.cleanup();
    }
  }

  async refreshSession(token: string): Promise<SessionResponse> {
    const request = this.requestController();
    try {
      const res = await fetch(new URL("/api/session/refresh", this.baseUrl), {
        method: "POST",
        headers: { authorization: `Bearer ${token}` },
        signal: request.controller.signal,
        credentials: "omit",
      });
      request.clearConnectionTimeout();
      if (!res.ok) {
        const detail = await this.parseError(res);
        throw new ChatAuthError(`Session refresh failed: ${res.status} ${detail}`);
      }
      return (await res.json()) as SessionResponse;
    } finally {
      request.cleanup();
    }
  }

  async sendMessageStream(
    token: string,
    chatId: string,
    message: string,
    onChunk?: (chunk: string, fullText: string) => void,
    onDone?: (result: SendMessageResult) => void,
    onStep?: (step: StepInfo) => void,
    externalSignal?: AbortSignal,
  ): Promise<string> {
    const request = this.requestController(externalSignal);
    try {
      const res = await fetch(new URL("/api/chat/send", this.baseUrl), {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ chat_id: chatId, message }),
        signal: request.controller.signal,
        credentials: "omit",
      });
      request.clearConnectionTimeout();

      if (res.status === 401) {
        throw new ChatAuthError("Chat session expired");
      }
      if (res.status === 429) {
        throw new RateLimitedError();
      }
      if (!res.ok) {
        const detail = await this.parseError(res);
        throw new Error(`Send failed: ${res.status} ${detail}`);
      }
      if (!res.body) {
        throw new Error("Streaming body is missing");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalText = "";
      let finalCitations: Citation[] = [];
      let done = false;

      while (!done) {
        const chunkResult = await reader.read();
        if (chunkResult.done) {
          done = true;
        } else {
          buffer += decoder.decode(chunkResult.value, { stream: true });
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";

          for (const frame of frames) {
            if (!frame.startsWith("data: ")) {
              continue;
            }
            const payload = JSON.parse(frame.slice(6)) as {
              text?: string;
              done?: boolean;
              citations?: unknown;
              step?: StepInfo;
            };
            if (payload.step && onStep) {
              onStep(payload.step);
            }
            const textPart = payload.text ?? "";
            if (textPart.length > 0 && payload.done !== true) {
              finalText += textPart;
              if (onChunk) {
                onChunk(textPart, finalText);
              }
            }
            if (payload.done === true) {
              finalText = payload.text ?? finalText;
              finalCitations = this.normalizeCitations(payload.citations);
            }
          }
        }
      }

      if (onDone) {
        onDone({ fullText: finalText, citations: finalCitations });
      }

      return finalText;
    } finally {
      request.cleanup();
    }
  }

  private normalizeCitations(rawCitations: unknown): Citation[] {
    if (!Array.isArray(rawCitations)) {
      return [];
    }

    return rawCitations.map((rawCitation) => {
      const source = (rawCitation ?? {}) as Record<string, unknown>;
      const rawMetadata = source.custom_metadata;
      const customMetadata: Record<string, string | number> = {};
      if (rawMetadata && typeof rawMetadata === "object" && !Array.isArray(rawMetadata)) {
        for (const [key, value] of Object.entries(rawMetadata as Record<string, unknown>)) {
          if (typeof value === "number" || typeof value === "string") {
            customMetadata[key] = value;
          }
        }
      }

      return {
        title: typeof source.title === "string" ? source.title : "",
        uri: typeof source.uri === "string" ? source.uri : "",
        text: typeof source.text === "string" ? source.text : "",
        customMetadata,
      };
    });
  }

  async injectMessage(
    token: string,
    chatId: string,
    role: "user" | "model",
    text: string,
  ): Promise<void> {
    const request = this.requestController();
    try {
      const res = await fetch(new URL("/api/chat/inject", this.baseUrl), {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ chat_id: chatId, role, text }),
        signal: request.controller.signal,
        credentials: "omit",
      });
      request.clearConnectionTimeout();
      if (res.status === 401) {
        throw new ChatAuthError("Chat session expired");
      }
      if (!res.ok) {
        const detail = await this.parseError(res);
        throw new Error(`Inject failed: ${res.status} ${detail}`);
      }
    } finally {
      request.cleanup();
    }
  }
}
